<template>
  <div class="restore-password-container">
    <div class="restore-password-form">
      <dx-load-indicator
        v-if="checkToken"
        :visible="checkToken"
      />
      <div v-else class="restore-password-header">{{ header }}</div>

      <dx-form
        v-if="isValidToken"
        :form-data="userCredentials"
        :read-only="loading"
        labelMode="floating"
        :width="300"
        @initialized="saveFormInstance"
      >
        <dx-simple-item data-field="password"
          :editor-options="editorsOptions.password"
        >
          <dx-label :text="$t('user.details.password')" :show-colon="false" />
          <dx-validation-rule type="pattern" :pattern="validationPatterns.password" :message="$t('general.badPassword')" />
          <dx-required-rule :message="$t('general.mandatory')" />
          <dx-string-length-rule :min="8" :max="20" :message="$t('general.badLengthPassword')" />
        </dx-simple-item>

        <dx-simple-item data-field="passwordConfirmation"
          name="passwordConfirmation"
          :editor-options="editorsOptions.passwordConfirmation"
        >
          <dx-label :text="$t('user.details.passwordConfirmation')" :show-colon="false" />
          <dx-required-rule :message="$t('general.mandatory')" />
          <dx-compare-rule :comparison-target="passwordComparison" :message="$t('user.details.msgPasswordsDoNotMatch')" />
          <dx-string-length-rule :min="8" :max="20" :message="$t('general.badLengthPassword')" />
        </dx-simple-item>
      </dx-form>

      <div
        v-if="isValidToken"
        class="dx-field"
      >
        <dx-button
          id="submit"
          type="default"
          width="100%"
          @click="restorePasswordClick"
          :disabled="loading"
        >
          <template>
            <div class="button-container">
              <dx-load-indicator
                :visible="loading"
                class="button-indicator"
              />
              <span class="dx-button-text">{{ $t('authentication.btnforgotPassword') }}</span>
            </div>
          </template>
        </dx-button>
      </div>
    </div>
  </div>
</template>

<script>
import DxForm, {
  DxSimpleItem,
  DxLabel,
  DxRequiredRule,
  DxCompareRule,
  DxStringLengthRule,
  DxValidationRule,
} from 'devextreme-vue/form';
import { DxButton, DxLoadIndicator } from 'devextreme-vue';
import Validator from 'devextreme/ui/validator';

import { notifier } from '@/shared/lib/notifier/index';

import axios from "axios";

import { validationPatterns } from "@/utils/validation-patterns";
import eyeOpen from "@/assets/eyeopen.svg";
import eyeClose from "@/assets/eyeclose.svg";

export default {
  components: {
    DxForm, DxSimpleItem, DxLabel, DxRequiredRule, DxCompareRule, DxStringLengthRule, DxValidationRule, DxButton, DxLoadIndicator
  },
  data() {
    return {
      validationPatterns,
      formInstance: null,
      userCredentials: {},
      token: null,
      checkToken: true,
      isValidToken: false,
      loading: false,
      header: this.$t('authentication.forgotPassFormHeader'),
      passwordMode: 'password',
      confirmPasswordMode: 'password',
      passwordIcon: eyeClose,
      confirmPasswordIcon: eyeClose,
    };
  },
  computed: {
    editorsOptions() {
      return {
        password: {
          mode: this.passwordMode,
          inputAttr: { autocomplete:'new-password' },
          maxLength: 20,
          buttons : [{
            name: 'password',
            options:{
              icon: this.passwordIcon,
              stylingMode: 'text',
              type: 'default',
              onClick: () => {
                // eslint-disable-next-line
                this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
                // eslint-disable-next-line
                this.passwordIcon = this.passwordIcon === eyeClose ? eyeOpen : eyeClose;
              }
            }
          }],
          onValueChanged: () => {
            const editor = this.formInstance.getEditor('passwordConfirmation');
            if (editor.option('value')) {
              const instance = Validator.getInstance(editor.element());
              instance.validate();
            }
          },
        },
        passwordConfirmation: {
          mode: this.confirmPasswordMode,
          inputAttr: { autocomplete:'new-password' },
          maxLength: 20,
          buttons : [{
            name: 'passwordConfirmation',
            options:{
              icon: this.confirmPasswordIcon,
              stylingMode: 'text',
              type: 'default',
              onClick: () => {
                // eslint-disable-next-line
                this.confirmPasswordMode = this.confirmPasswordMode === 'text' ? 'password' : 'text';
                // eslint-disable-next-line
                this.confirmPasswordIcon = this.confirmPasswordIcon === eyeClose ? eyeOpen : eyeClose;
              }
            }
          }],
        },
      }
    },
  },
  methods: {
    saveFormInstance(e) {
      this.formInstance = e.component;
    },
    validate() {
      const result = this.formInstance.validate();
      return result.isValid;
    },
    passwordComparison() {
      if (this.userCredentials.password !== undefined)
        return this.userCredentials.password;
      else
        return false;
    },
    restorePasswordClick() {
      if (!this.validate()) {
        return;
      }

      this.loading = true;
      axios.post(`${this.$store.getters.apiPath}/users/restore-password/${this.token}`, this.userCredentials.password, {headers:{'Content-Type' : 'text/plain'}})
      .then((res) => {
        notifier.showSuccess(res.data.message);
        this.$router.push({ path: '/login-form'});
        this.loading = false;
      })
      .catch((err) => {
        notifier.showError(err.response.data.localizedMessage);
        this.$router.push({ path: '/login-form'});
        this.loading = false;
      });
    }
  },
  created() {
  },
  watch: {
    '$route.params': {
      handler: async function(params) {
        this.token = params.token;
        if (this.token) {
          axios.get(`${this.$store.getters.apiPath}/users/restore-password/${this.token}/check-token`).then(() => {
            this.isValidToken = true;
            this.checkToken = false;
          })
          .catch((err) => {
            this.checkToken = false;
            this.header = err.response.data.localizedMessage;
            setTimeout(() => this.$router.push({ path: '/login-form'}), 3000);
          });
        } else {
          this.$router.push({ path: '/login-form'});
        }
      },
      deep: true,
      immediate: true
    },
  }
};
</script>

<style lang="scss">
@import "../themes/generated/variables.base.scss";
@import url("../themes/fonts.css");

.restore-password-container {
  display: flex;
  min-height: 100vh;
  width: 100vw;
  justify-content: center;
  align-items: center;

  .restore-password-form {
    display: flex;
    flex-direction: column;
    background-color: #fff;
    padding: 48px 48px 50px 48px;
    border-radius: 20px;
    box-shadow: 0 5px 10px 0 rgba(0, 0, 0, .5);

    .restore-password-header {
      text-align: center;
      margin-bottom: 8px;
      font-family: 'Golos Text', sans-serif;
      font-size: 20px;
      font-weight: 400;
      line-height: 24px;
    }
  }
}

#submit {
  font-family: 'Golos Text', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  margin-top: 10px;
}
.button-indicator {
  height: 24px;
  width: 24px;
  display: inline-block;
  vertical-align: middle;
  margin-right: 5px;
  .dx-loadindicator-icon {
    .dx-loadindicator-segment {
      background: white;
    }
  }
}

.button-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}

.validation-margin {
  margin-bottom: 2.5rem;
}

</style>
